import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { stanjeRobeStatusAPI, stanjeRobeStockOutAPI } from '../services/api';
import { StanjeRobeStockOutRecord } from '../types';
import { showCustomAlert } from './CustomAlert';
import { formatM2, formatStanjeRobeDate } from '../utils/stanjeRobeCalc';
import { groupStockOutShipments, StanjeRobeShipmentGroup } from '../utils/stanjeRobeShipments';
import { getCustomerBrandNames } from '../utils/stanjeRobeCustomerBrands';
import { useLanguage } from '../contexts/LanguageContext';

const StanjeRobeCustomerShipments: React.FC = () => {
  const { t } = useLanguage();
  const [records, setRecords] = useState<StanjeRobeStockOutRecord[]>([]);
  const [brandNames, setBrandNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [viewVisible, setViewVisible] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState<StanjeRobeShipmentGroup | null>(null);

  const shipments = useMemo(() => groupStockOutShipments(records), [records]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [shipmentsData, profile] = await Promise.all([
        stanjeRobeStockOutAPI.getAll(),
        stanjeRobeStatusAPI.getMyProfile().catch(() => null)
      ]);
      setRecords(Array.isArray(shipmentsData) ? shipmentsData : []);
      setBrandNames(profile ? getCustomerBrandNames(profile) : []);
    } catch (err: any) {
      const msg = err?.response?.data?.message || t('errors.loadShipments');
      setError(msg);
      showCustomAlert.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const openView = (group: StanjeRobeShipmentGroup) => {
    setSelectedShipment(group);
    setViewVisible(true);
  };

  const actionsBody = (group: StanjeRobeShipmentGroup) => (
    <Button icon="pi pi-eye" className="p-button-sm" onClick={() => openView(group)} />
  );

  const detailHeader = selectedShipment
    ? t('shipments.detailTitle', { date: formatStanjeRobeDate(selectedShipment.outDate) })
    : t('shipments.detailTitleFallback');

  return (
    <div className="p-4">
      <div className="flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="m-0">{t('shipments.title')}</h2>
          <div className="text-600 mt-1">{t('shipments.subtitle')}</div>
          {brandNames.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {brandNames.map((name) => (
                <Tag key={name} value={name} severity="info" icon="pi pi-tag" />
              ))}
            </div>
          )}
        </div>
        <Button label={t('shipments.refresh')} icon="pi pi-refresh" outlined severity="secondary" onClick={load} loading={loading} />
      </div>

      {error && <Message severity="error" text={error} className="mb-3" />}

      <Card>
        <DataTable
          value={shipments}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
          emptyMessage={t('shipments.empty')}
          className="p-datatable-sm"
          sortField="outDate"
          sortOrder={-1}
          responsiveLayout="stack"
          breakpoint="768px"
        >
          <Column header={t('shipments.date')} body={(g: StanjeRobeShipmentGroup) => formatStanjeRobeDate(g.outDate)} sortable sortField="outDate" style={{ width: '130px' }} />
          <Column header={t('shipments.itemCount')} body={(g: StanjeRobeShipmentGroup) => g.items.length} style={{ width: '120px' }} />
          <Column header={t('shipments.totalM2')} body={(g: StanjeRobeShipmentGroup) => formatM2(g.totalM2)} style={{ width: '120px' }} />
          <Column header={t('shipments.actions')} body={actionsBody} style={{ width: '90px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={detailHeader}
        visible={viewVisible}
        onHide={() => setViewVisible(false)}
        style={{ width: 'min(720px, 96vw)' }}
        footer={<Button label={t('shipments.close')} className="p-button-secondary p-button-outlined" onClick={() => setViewVisible(false)} />}
        modal
        draggable={false}
      >
        {!selectedShipment ? (
          <div>—</div>
        ) : (
          <div className="p-fluid">
            <div className="grid mb-3">
              <div className="col-12 md:col-6">
                <div className="text-600">{t('shipments.date')}</div>
                <div style={{ fontWeight: 700 }}>{formatStanjeRobeDate(selectedShipment.outDate)}</div>
              </div>
              <div className="col-12 md:col-6">
                <div className="text-600">{t('shipments.totalM2')}</div>
                <div style={{ fontWeight: 700 }}>{formatM2(selectedShipment.totalM2)}</div>
              </div>
            </div>

            {selectedShipment.notes && (
              <div className="mb-3">
                <div className="text-600">{t('shipments.note')}</div>
                <div>{selectedShipment.notes}</div>
              </div>
            )}

            <h3 className="m-0 mb-2">{t('shipments.items')}</h3>
            <DataTable value={selectedShipment.items} className="p-datatable-sm" emptyMessage={t('shipments.noItems')} responsiveLayout="stack" breakpoint="768px">
              <Column field="productName" header={t('shipments.product')} />
              <Column header={t('shipments.pallet')} body={(r: StanjeRobeStockOutRecord) => r.quantity} style={{ width: '100px' }} />
              <Column header={t('shipments.m2')} body={(r: StanjeRobeStockOutRecord) => formatM2(r.squareMeters ?? 0)} style={{ width: '110px' }} />
            </DataTable>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default StanjeRobeCustomerShipments;
